/**
 * Light Hunt Studio lives on its own subdomain (studio.lightshunt.com) and the
 * marketplace on the apex. Both origins are served by the same deployment, so
 * the SPA picks its route tree from the hostname.
 *
 * The sibling origin is derived from the current one: lightshunt.com and
 * studio.lightshunt.com in production, localhost:5173 and studio.localhost:5173
 * in development (browsers resolve *.localhost to loopback).
 *
 * ponytail: *.vercel.app preview hosts have no studio sibling, so Studio pages
 * are unreachable on previews. Add a preview alias or env override if previews
 * ever need Studio.
 */

const STUDIO_PREFIX = 'studio.';

const currentLocation = () =>
  typeof window === 'undefined' ? null : window.location;

export function isStudioHostname(hostname: string): boolean {
  return hostname.toLowerCase().startsWith(STUDIO_PREFIX);
}

export function isStudioHost(): boolean {
  const location = currentLocation();
  return location ? isStudioHostname(location.hostname) : false;
}

/** The studio origin for a given origin, e.g. https://www.lightshunt.com -> https://studio.lightshunt.com. */
export function toStudioOrigin(origin: string): string {
  const url = new URL(origin);
  if (!isStudioHostname(url.hostname)) {
    url.hostname = `${STUDIO_PREFIX}${url.hostname.replace(/^www\./i, '')}`;
  }
  return url.origin;
}

/** The marketplace origin for a given origin, e.g. https://studio.lightshunt.com -> https://lightshunt.com. */
export function toMainOrigin(origin: string): string {
  const url = new URL(origin);
  if (isStudioHostname(url.hostname)) {
    url.hostname = url.hostname.slice(STUDIO_PREFIX.length);
  }
  return url.origin;
}

/** Absolute studio URL for a studio-relative path ('/projects', '/p/abc'). */
export function studioUrl(path = '/'): string {
  const location = currentLocation();
  const origin = location ? toStudioOrigin(location.origin) : 'https://studio.lightshunt.com';
  return `${origin}${path}`;
}

/** Absolute marketplace URL for a marketplace path ('/sequences', '/profile'). */
export function mainSiteUrl(path = '/'): string {
  const location = currentLocation();
  const origin = location ? toMainOrigin(location.origin) : 'https://lightshunt.com';
  return `${origin}${path}`;
}

/**
 * Where a legacy marketplace path lands on the studio host. The app used to
 * live under /studio and the marketing page under /installers; invitation and
 * presentation links already sent to people keep their paths.
 */
export function legacyPathToStudioPath(pathname: string): string {
  if (pathname === '/installers') return '/';
  if (pathname === '/studio') return '/projects';
  if (pathname === '/studio/settings') return '/settings';
  if (pathname.startsWith('/studio/')) return pathname.slice('/studio'.length);
  return pathname;
}
